/**
 * 教师上帝视角控制台（新布局 v2）。
 *
 * 布局原则（docs/design/ui-layouts/wf-04/05/07）：
 * - 顶部固定传输条：开始/暂停/下一轮/揭示/重置/导出，永不随页面滚走；
 * - Tab 化：Live / DAG / Material / Review；Live 不再被 DAG 与历史信息挤占；
 * - 房间设置以抽屉形式打开；辩题/资料库独立成 Material 页；
 * - 所有面板保持挂载（hidden），避免丢失本地状态并兼容既有测试。
 */
import { useState } from 'react';
import { AI_GLASSES_MOTION } from '@zongheng/core-schema';
import { TopBar } from '../app/TopBar.tsx';
import { useArena } from '../state/ArenaContext.tsx';
import { Badge, Button, Panel } from '../components/ui/primitives.tsx';
import { StageControlBar } from '../components/coach-console/StageControlBar.tsx';
import { DualClockPanel } from '../components/coach-console/DualClockPanel.tsx';
import { StageTimeline } from '../components/coach-console/StageTimeline.tsx';
import { DagBoard } from '../components/coach-console/DagBoard.tsx';
import { ParticipantsPanel } from '../components/coach-console/ParticipantsPanel.tsx';
import { CommitmentPanel } from '../components/coach-console/CommitmentPanel.tsx';
import { AlertFeed } from '../components/coach-console/AlertFeed.tsx';
import { EvidenceCoveragePanel } from '../components/coach-console/EvidenceCoveragePanel.tsx';
import { AnnotationPanel } from '../components/coach-console/AnnotationPanel.tsx';
import { ScoreTable } from '../components/coach-console/ScoreTable.tsx';
import { ReviewAnalyticsPanel } from '../components/coach-console/ReviewAnalyticsPanel.tsx';
import { RoomSettingsPanel } from '../components/coach-console/RoomSettingsPanel.tsx';
import { MotionLibraryPanel } from '../components/coach-console/MotionLibraryPanel.tsx';
import { MediaDock } from '../components/system/MediaDock.tsx';
import { VideoWall } from '../components/system/VideoWall.tsx';
import { useCoachScores } from '../components/coach-console/scoreStore.ts';
import { buildMatchReportMarkdown, copyMarkdown, downloadMarkdown } from '../export/report.ts';

const REPORT_FILENAME = 'zongheng-match-report.md';
type CoachTab = 'live' | 'dag' | 'material' | 'review';

const TABS: { key: CoachTab; label: string; hint: string }[] = [
  { key: 'live', label: 'Live', hint: '计时 / 发牌 / 告警 / 批注' },
  { key: 'dag', label: 'DAG', hint: '攻防树与论据路径' },
  { key: 'material', label: 'Material', hint: '辩题 / 资料库 / 知识卡' },
  { key: 'review', label: 'Review', hint: '评分与战报' },
];

export default function CoachConsolePage() {
  const { state, actions } = useArena();
  const motion = AI_GLASSES_MOTION;
  const scores = useCoachScores();
  const [tab, setTab] = useState<CoachTab>('live');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportMarkdown, setReportMarkdown] = useState('');
  const [exportNote, setExportNote] = useState('');
  const [copied, setCopied] = useState(false);

  const handleExportReport = async () => {
    if (exporting) return;
    setExporting(true);
    setCopied(false);
    setExportNote('正在生成《纵横杯跟场诊断战报》…');

    // 1) 本地构建带跟场评分的完整 Markdown（纯函数），立即触发下载（浏览器 E2E 依赖 download 事件）。
    const markdown = buildMatchReportMarkdown(motion, state, { scores });
    setReportMarkdown(markdown);
    downloadMarkdown(REPORT_FILENAME, markdown);

    try {
      // 2) 调用后端 exportReport：P2P 后端负责归档/落盘，Mock 后端会给出演示 toast。
      let backendNote = '';
      try {
        await actions.exportReport();
        backendNote = '已调用后端 exportReport()';
      } catch (error) {
        backendNote = `后端 exportReport() 失败：${
          error instanceof Error ? error.message : String(error)
        }`;
      }
      // 3) 复制本地完整战报到剪贴板作为最终内容（后端实现可能复制精简版，故最后覆盖）。
      const copiedOk = await copyMarkdown(markdown);
      setCopied(copiedOk);
      setExportNote(
        copiedOk
          ? `战报已下载并复制到剪贴板（${REPORT_FILENAME}；${backendNote}）。`
          : `战报已下载为 ${REPORT_FILENAME}（剪贴板不可用；${backendNote}）。`,
      );
      setTab('review');
    } finally {
      setExporting(false);
    }
  };

  const handleCopy = async () => {
    const markdown = reportMarkdown || buildMatchReportMarkdown(motion, state, { scores });
    const ok = await copyMarkdown(markdown);
    setCopied(ok);
    setExportNote(ok ? '已复制战报到剪贴板。' : '剪贴板不可用，请使用「下载 .md」。');
  };

  const handleDownload = () => {
    const markdown = reportMarkdown || buildMatchReportMarkdown(motion, state, { scores });
    downloadMarkdown(REPORT_FILENAME, markdown);
    setReportMarkdown(markdown);
    setExportNote(`已触发 Markdown 下载（${REPORT_FILENAME}）。`);
  };

  return (
    <div className="min-h-screen">
      <TopBar compact />
      <main className="mx-auto max-w-[1620px] space-y-3 p-3 sm:p-4">
        <div className="flex flex-wrap items-end justify-between gap-3 px-1">
          <div>
            <div className="flex items-center gap-2">
              <span className="status-dot status-dot--live" />
              <span className="micro-label">COACH CONSOLE · MASTER AUTHORITY</span>
            </div>
            <h1 className="mt-1 text-[22px] font-semibold tracking-tight text-metal">
              教练台 · 上帝视角控制台
            </h1>
            <p className="mt-0.5 max-w-3xl text-[11px] text-slate-500">{motion.motion}</p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone="blind">双盲发牌 {Object.keys(state.commitments).length} 对</Badge>
            <Badge tone="pass">已完成 {state.completedStages.length}/16</Badge>
            <Badge tone={state.alerts.length > 0 ? 'violation' : 'neutral'}>
              告警 {state.alerts.length}
            </Badge>
            <Badge tone="warning">批注 {state.annotations.length}</Badge>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setSettingsOpen((open) => !open)}
              testId="open-room-settings"
            >
              房间设置
            </Button>
          </div>
        </div>

        <div className="coach-command">
          <StageControlBar onExportReport={handleExportReport} exporting={exporting} />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="coach-tabs" data-testid="coach-tabs" role="tablist" aria-label="教练台视图">
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  data-testid={`coach-tab-${item.key}`}
                  data-active={tab === item.key}
                  aria-selected={tab === item.key}
                  title={item.hint}
                  className="coach-tab"
                  onClick={() => setTab(item.key)}
                >
                  {item.label}
                </button>
              ))}
              <span className="ml-2 hidden text-[10px] text-slate-500 lg:inline">
                {TABS.find((item) => item.key === tab)?.hint}
              </span>
            </div>
            <span className="text-[10px] text-slate-500">
              {state.phase === 'RUNNING'
                ? '比赛进行中 · Tab 切换不打断计时'
                : '顶部控制条始终可用'}
            </span>
          </div>

          <MediaDock prefix="coach" compact />
        </div>

        <div className="relative">
          {/* Live：只放“本环节需要盯”的东西 */}
          <section hidden={tab !== 'live'} aria-label="Live 视图">
            <div className="workspace-shell">
              <div className="space-y-3">
                <VideoWall prefix="coach" compact />
                <DualClockPanel />
                <StageTimeline />
                <AlertFeed />
              </div>
              <aside className="workspace-rail space-y-3">
                <ParticipantsPanel />
                <CommitmentPanel />
                <AnnotationPanel motion={motion} />
              </aside>
            </div>
          </section>

          {/* DAG：全宽画布，控制条不离开视口 */}
          <section hidden={tab !== 'dag'} aria-label="DAG 视图" className="space-y-3">
            <DagBoard motion={motion} currentStageIndex={state.stageIndex} />
            <div className="lens px-4 py-3">
              <div className="micro-label">DAG READING</div>
              <p className="mt-1 text-[11px] text-slate-400">
                节点宽度 ≥210px、正文换行不截断；激活路径使用流光边。点击节点查看论据与反制。
                时间轴与双表仍在顶部/ Live 中，切换 Tab 不会暂停比赛。
              </p>
            </div>
          </section>

          {/* Material：辩题、资料库、知识卡、DAG 摘要 */}
          <section hidden={tab !== 'material'} aria-label="资料库视图" className="space-y-3">
            <MotionLibraryPanel motion={motion} onOpenDag={() => setTab('dag')} />
            <EvidenceCoveragePanel motion={motion} />
          </section>

          {/* Review：评分与战报 */}
          <section hidden={tab !== 'review'} aria-label="复盘视图" className="space-y-3">
            <ReviewAnalyticsPanel motion={motion} />
            <ScoreTable motion={motion} />
            {reportMarkdown ? (
              <Panel
                title="战报导出预览"
                subtitle="Markdown 纯文本 · 可直接粘贴到复盘文档"
                testId="coach-report-preview-panel"
                actions={
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="secondary" testId="copy-report" onClick={handleCopy}>
                      {copied ? '已复制' : '复制 Markdown'}
                    </Button>
                    <Button size="sm" variant="secondary" testId="download-report" onClick={handleDownload}>
                      下载 .md
                    </Button>
                  </div>
                }
              >
                <p className="mb-2 text-xs text-slate-400" data-testid="export-status">
                  {exportNote}
                </p>
                <pre
                  data-testid="report-preview"
                  className="mono max-h-96 overflow-auto whitespace-pre-wrap rounded-[10px] bg-white/[0.018] p-3 text-[11px] leading-relaxed text-slate-300 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]"
                >
                  {reportMarkdown}
                </pre>
              </Panel>
            ) : (
              <p className="text-center text-xs text-slate-500" data-testid="export-status">
                {exportNote || '比赛过程中随时可导出《纵横杯跟场诊断战报》。'}
              </p>
            )}
          </section>
        </div>
      </main>

      {settingsOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/45 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-label="房间设置"
          onClick={() => setSettingsOpen(false)}
        >
          <aside
            className="lens h-full w-[380px] max-w-[92vw] overflow-auto p-4 pb-6"
            onClick={(event) => event.stopPropagation()}
          >
            <RoomSettingsPanel onClose={() => setSettingsOpen(false)} />
          </aside>
        </div>
      )}
    </div>
  );
}
